import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, useAction } from "../../../components/ui";
import { t, ta } from "../../../lib/sprache";
import { date, today } from "../../../lib/format";
import { SELF_BILLING_E_INVOICE_BASE_YEAR, SELF_BILLING_E_INVOICE_DEFAULT_FROM, selfBillingEInvoiceSuggestion } from "@talentkontor/domain";
import { loadMahnleiter, loadOwnInvoicesYear } from "./data";
import { useOrgCountry } from "./Datev";
import { DEFAULT_NUMBER_TEMPLATE, gutschriftStichtagHinweis, invoiceNumberExample } from "./model";
import { KonditionenTeil } from "./Konditionen";
import { useAngebotsEinstellungen } from "../anfragen/hooks";
import { AngebotsNummer } from "../anfragen/Einstellungen";

/**
 * Nummernkreise (IA-1, Zoho-Vorbild): EINE Tabelle aller Belegarten mit Präfix, Muster, nächster Nummer, Neustart und
 * Vorschau; „Bearbeiten“ öffnet je Zeile ein Fenster mit denselben Speicherwegen wie bisher (Rechnung: Konditionen und
 * invoice_number_set_next, Gutschrift: org_settings.self_billing_number_template, Angebot: org_offer_settings und
 * offer_number_set_next). Die Datenbank vergibt die Nummer erst beim Festschreiben (0028/0037); hochsetzen geht nur aufwärts.
 */
type Belegart = "rechnung" | "gutschrift" | "angebot";

/** Wann die laufende Nummer neu beginnt, aus dem Muster ({MM} monatlich, {YYYY}/{YY} jährlich, sonst fortlaufend). */
export function neustartText(muster: string | null | undefined, scope?: "all" | "year" | "month"): string {
  const s = scope ?? (/\{MM\}/.test(muster ?? "") ? "month" : /\{YY(YY)?\}/.test(muster ?? "") ? "year" : "all");
  return s === "month" ? t("jeden Monat", "every month") : s === "year" ? t("jedes Jahr", "every year") : t("fortlaufend", "continuous");
}

export function NummernkreisTabelle({ orgId, creator = false }: { orgId: string; creator?: boolean }) {
  const heute = today();
  const [offen, setOffen] = useState<Belegart | null>(null);
  const konditionen = useQuery({ queryKey: ["org_settings", orgId, "konditionen"], queryFn: () => loadMahnleiter(api, orgId) });
  const rechnung = useQuery({
    queryKey: ["invoice_number_next", orgId, "now"],
    queryFn: () => api.read<{ next: number; number: string; scope: "all" | "year" | "month" }>("invoice_number_next", { p_org: orgId }),
  });
  const gs = useQuery({
    queryKey: ["org_settings", orgId, "self_billing"], enabled: !creator,
    queryFn: () => api.select<{ self_billing_number_template: string | null }>("org_settings", { columns: "self_billing_number_template", filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null),
  });
  const angebot = useAngebotsEinstellungen(orgId);
  const angebotNaechste = useQuery({
    queryKey: ["org_offer_settings", orgId, "nummer", angebot.data?.updated_at ?? ""], enabled: !!angebot.data,
    queryFn: () => api.read<{ next: number; number: string; scope: string }>("offer_number_next", { p_org: orgId }),
  });
  const s = konditionen.data?.settings;
  const rMuster = s?.invoice_number_scheme?.template || DEFAULT_NUMBER_TEMPLATE;
  const gMuster = gs.data?.self_billing_number_template || "GS-{YYYY}-{N}";
  const zeilen: { key: Belegart; art: string; praefix: string; muster: string; naechste: string; neustart: string; vorschau: string }[] = [
    {
      key: "rechnung", art: t("Rechnung", "Invoice"), praefix: s?.invoice_number_prefix || "–", muster: rMuster,
      naechste: rechnung.data ? String(rechnung.data.next) : "…", neustart: neustartText(rMuster, rechnung.data?.scope),
      vorschau: rechnung.data?.number ?? invoiceNumberExample(rMuster, s?.invoice_number_prefix ?? "", heute),
    },
    ...(creator ? [] : [{
      key: "gutschrift" as const, art: t("Gutschrift", "Self-billing invoice"), praefix: "–", muster: gMuster,
      naechste: t("beim Erstellen", "on creation"), neustart: neustartText(gMuster), vorschau: invoiceNumberExample(gMuster, "", heute),
    }]),
    {
      key: "angebot", art: t("Angebot (Anfragen)", "Offer (inquiries)"), praefix: angebot.data?.number_prefix || "–", muster: angebot.data?.number_template ?? "…",
      naechste: angebotNaechste.data ? String(angebotNaechste.data.next) : "…", neustart: neustartText(angebot.data?.number_template),
      vorschau: angebotNaechste.data?.number ?? (angebot.data ? invoiceNumberExample(angebot.data.number_template, angebot.data.number_prefix, heute) : "…"),
    },
  ];
  const titel: Record<Belegart, string> = {
    rechnung: t("Nummernkreis der Rechnungen", "Invoice number range"),
    gutschrift: t("Nummernkreis der Gutschriften", "Self-billing number range"),
    angebot: t("Nummernkreis der Angebote", "Offer number range"),
  };
  return (
    <section className="panel stack tk-nummernkreise" aria-labelledby="nummernkreis-title">
      <h2 id="nummernkreis-title">{t("Alle Belegarten", "All document types")}</h2>
      {/* N-1: im schmalen Rahmen (bei 1024 px) fallen Präfix und Muster als Spalten weg; das Muster steht unter der Vorschau */}
      <div className="tablewrap tk-cardtable tk-rahmen">
        <table>
          <caption className="sr-only">{t("Nummernkreise aller Belegarten", "Number ranges of all document types")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Belegart", "Document type")}</th>
              <th scope="col" className="tk-r-weg">{t("Präfix", "Prefix")}</th>
              <th scope="col" className="tk-r-weg">{t("Muster", "Pattern")}</th>
              <th scope="col">{t("Nächste Nummer", "Next number")}</th>
              <th scope="col">{t("Neustart", "Restart")}</th>
              <th scope="col">{t("Vorschau", "Preview")}</th>
              <th scope="col"><span className="sr-only">{t("Aktion", "Action")}</span></th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.key}>
                <td data-label={t("Belegart", "Document type")}><strong>{z.art}</strong></td>
                <td data-label={t("Präfix", "Prefix")} className="tk-r-weg"><span className="mono">{z.praefix}</span></td>
                <td data-label={t("Muster", "Pattern")} className="tk-r-weg"><span className="mono">{z.muster}</span></td>
                <td data-label={t("Nächste Nummer", "Next number")}>{z.naechste}</td>
                <td data-label={t("Neustart", "Restart")}>{z.neustart}</td>
                <td data-label={t("Vorschau", "Preview")} ><span className="mono">{z.vorschau}</span><small className="tk-r-alt">{t("Muster ", "Pattern ")}<span className="mono">{z.muster}</span></small></td>
                <td data-label={t("Aktion", "Action")}><button type="button" onClick={() => setOffen(z.key)}>{t("Bearbeiten", "Edit")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <small>{t("Bausteine im Muster: {P} Präfix, {YYYY} oder {YY} Jahr, {MM} Monat, {N} laufende Nummer ({N:4} mindestens vierstellig). Mit Jahr oder Monat im Muster beginnt die Nummer neu, sonst zählt sie fortlaufend weiter. Verträge zählen fortlaufend und lassen sich nicht einstellen.",
        "Building blocks in the pattern: {P} prefix, {YYYY} or {YY} year, {MM} month, {N} sequential number ({N:4} at least four digits). With a year or month in the pattern the number restarts, otherwise it keeps counting. Contracts are numbered continuously and cannot be configured.")}</small>
      <Dialog open={offen !== null} onClose={() => setOffen(null)} title={offen ? titel[offen] : ""} wide>
        {offen === "rechnung" && (
          <div className="stack">
            <KonditionenTeil orgId={orgId} creator={creator} teile={["nummer"]} seite="nummernkreise" title={titel.rechnung} kopf={false} />
            <RechnungNaechsteNummer orgId={orgId} />
            <Rueckdatierung orgId={orgId} />
          </div>
        )}
        {offen === "gutschrift" && <GutschriftMuster orgId={orgId} />}
        {offen === "angebot" && <AngebotsNummer orgId={orgId} today={heute} />}
      </Dialog>
    </section>
  );
}

/**
 * Nächste Rechnungsnummer ansehen und beim Umstieg aus einem anderen Rechnungsprogramm einmalig hochsetzen (nur aufwärts,
 * damit keine Nummer doppelt vergeben wird). 0055: auch für das nächste Jahr (Umstieg zum 1. Januar vorbereiten).
 */
export function RechnungNaechsteNummer({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [forYear, setForYear] = useState<"now" | "next">("now");
  const nextYearStart = `${new Date().getFullYear() + 1}-01-01`;
  const q = useQuery({
    queryKey: ["invoice_number_next", orgId, forYear],
    queryFn: () => api.read<{ next: number; number: string; scope: "all" | "year" | "month" }>("invoice_number_next", { p_org: orgId, ...(forYear === "next" ? { p_date: nextYearStart } : {}) }),
  });
  const [value, setValue] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  const scope = q.data?.scope === "month" ? (forYear === "next" ? t("im Januar", "in January") : t("in diesem Monat", "this month")) : q.data?.scope === "year" ? (forYear === "next" ? t("im nächsten Jahr", "next year") : t("in diesem Jahr", "this year")) : t("fortlaufend", "continuous");

  async function submit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const n = Number(value.trim());
    if (!Number.isInteger(n) || n < 1) return setFehler(t("Bitte eine ganze Zahl ab 1 eingeben.", "Please enter a whole number from 1."));
    if (q.data && n <= q.data.next - 1) return setFehler(t(`Die nächste Nummer muss größer als ${q.data.next - 1} sein.`, `The next number must be greater than ${q.data.next - 1}.`));
    setFehler(null);
    const ok = await run("nummer", () => api.rpc("invoice_number_set_next", { p_org: orgId, p_next: n, ...(forYear === "next" ? { p_date: nextYearStart } : {}) }),
      forYear === "next" ? t("Nächste Rechnungsnummer für das nächste Jahr gesetzt.", "Next invoice number for next year set.") : t("Nächste Rechnungsnummer gesetzt.", "Next invoice number set."));
    if (ok !== undefined) {
      setValue("");
      await qc.invalidateQueries({ queryKey: ["invoice_number_next", orgId] });
    }
  }

  return (
    <section className="panel stack" aria-labelledby="naechste-nummer-title">
      <h3 id="naechste-nummer-title">{t("Nächste Rechnungsnummer", "Next invoice number")}</h3>
      <p>
        {q.data ? <>{ta("Die nächste Rechnung der Agentur erhält ", "The agency's next invoice gets ", "Deine nächste Rechnung erhält ", "Your next invoice gets ")}<strong className="num">{q.data.number}</strong> {t("(laufende Nummer ", "(sequential number ")}{q.data.next}, {scope}).</> : q.error ? t("Die nächste Nummer lässt sich gerade nicht anzeigen.", "The next number cannot be shown right now.") : t("Nächste Nummer wird geladen …", "Loading next number …")}
      </p>
      <form className="row wrap" onSubmit={(e) => void submit(e)} noValidate>
        {q.data?.scope !== "all" && (
          <label>
            {t("Gilt für", "Applies to")}<select value={forYear} onChange={(e) => { setForYear(e.target.value as "now" | "next"); setValue(""); setFehler(null); }}>
              <option value="now">{t("das laufende Jahr", "the current year")}</option>
              <option value="next">{t("das nächste Jahr (ab 1. Januar)", "next year (from 1 January)")}</option>
            </select>
          </label>
        )}
        <label>
          {t("Nächste laufende Nummer", "Next sequential number")}<input inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} placeholder={q.data ? String(q.data.next) : ""} aria-describedby="nummernkreis-hinweis" />
        </label>
        <button type="submit" disabled={!!busy || !value.trim()}>{busy ? t("Wird gesetzt …", "Setting …") : t("Übernehmen", "Take over")}</button>
      </form>
      <small id="nummernkreis-hinweis">{t("Für den Umstieg aus einem anderen Rechnungsprogramm: Die Nummer lässt sich nur erhöhen, nie senken.", "For switching from another invoicing program: the number can only be increased, never decreased.")}</small>
      {(fehler || error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
    </section>
  );
}

/** Rückfrage beim Festschreiben, wenn das Rechnungsdatum weit zurückliegt (Befund 31, sonst 30 Tage) */
function Rueckdatierung({ orgId }: { orgId: string }) {
  const q = useQuery({
    queryKey: ["org_settings", orgId, "backdate"],
    queryFn: () => api.select<{ backdate_warn_days: number | null }>("org_settings", { columns: "backdate_warn_days", filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null),
  });
  const [value, setValue] = useState<string | null>(null);
  const { busy, error, attempt } = useAction();
  const v = value ?? (q.data?.backdate_warn_days != null ? String(q.data.backdate_warn_days) : "");
  const n = v.trim() === "" ? null : Number(v);
  const valid = n === null || (Number.isInteger(n) && n >= 1 && n <= 365);
  return (
    <form className="stack" onSubmit={(e) => {
      e.preventDefault();
      if (!valid) return;
      void attempt("rueck", () => api.update("org_settings", [["org_id", "eq", orgId]], { backdate_warn_days: n }), t("Gespeichert.", "Saved.")).then((ok) => { if (ok) setValue(null); });
    }}>
      <label>
        {t("Rückfrage, wenn das Rechnungsdatum mehr als so viele Tage zurückliegt", "Ask if the invoice date is more than this many days in the past")}<input className="num" inputMode="numeric" value={v} onChange={(e) => setValue(e.target.value)} placeholder="30" aria-invalid={!valid} />
      </label>
      {!valid && <p className="tk-error" role="alert">{t("Bitte eine ganze Zahl von 1 bis 365 eingeben oder leer lassen (30 Tage).", "Please enter a whole number from 1 to 365 or leave empty (30 days).")}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row"><button type="submit" disabled={!!busy || value === null || !valid}>{t("Speichern", "Save")}</button></div>
    </form>
  );
}

/** Muster der Gutschriftnummern (0046): eigener Kreis neben den Rechnungen der Agentur */
export function GutschriftMuster({ orgId }: { orgId: string }) {
  const q = useQuery({
    queryKey: ["org_settings", orgId, "self_billing"],
    queryFn: () => api.select<{ self_billing_number_template: string | null }>("org_settings", { columns: "self_billing_number_template", filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null),
  });
  const [value, setValue] = useState<string | null>(null);
  const { busy, error, attempt } = useAction();
  const current = q.data?.self_billing_number_template ?? "";
  const v = value ?? current;
  const valid = !v.trim() || (/\{N(:[1-8])?\}/.test(v) && v.length <= 60);
  return (
    <form className="stack" onSubmit={(e) => {
      e.preventDefault();
      if (!valid) return;
      void attempt("gs", () => api.update("org_settings", [["org_id", "eq", orgId]], { self_billing_number_template: v.trim() || null }), t("Muster der Gutschriftnummern gespeichert.", "Self-billing invoice number pattern saved."))
        .then((ok) => { if (ok) setValue(null); });
    }}>
      <h3>{t("Gutschriften", "Self-billing invoices")}</h3>
      <label>
        {t("Muster der Gutschriftnummern", "Self-billing invoice number pattern")}<input value={v} onChange={(e) => setValue(e.target.value)} placeholder="GS-{YYYY}-{N}" aria-describedby="gs-hinweis" aria-invalid={!valid} />
      </label>
      <small id="gs-hinweis">{t("Gutschriften (Abrechnung für den Creator) haben einen eigenen Nummernkreis. Platzhalter: ", "Self-billing invoices (statement for the creator) have their own number range. Placeholders: ")}{"{YYYY}"}, {"{YY}"}, {"{MM}"}, {"{N}"} {t("oder ", "or ")}{"{N:4}"}{t(". Leer: GS-", ". Empty: GS-")}{"{YYYY}"}-{"{N}"}.</small>
      {!valid && <p className="tk-error" role="alert">{t("Das Muster braucht die laufende Nummer ", "The pattern needs the sequential number ")}{"{N}"} {t("und höchstens 60 Zeichen.", "and at most 60 characters.")}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row"><button type="submit" disabled={!!busy || value === null || !valid}>{t("Speichern", "Save")}</button></div>
    </form>
  );
}

/**
 * K-417c (0335): Ab wann Gutschriften an Creator nach der Vorgabe zusätzlich als E-Rechnung (XRechnung) gehen. Die Vorgabe
 * betrifft Creator in Deutschland, die regelbesteuert sind (keine Kleinunternehmer), und Agenturen in Deutschland; am Creator
 * lässt sie sich überschreiben (Creator-Profil · Gutschriftverfahren). Leer = 1.1.2028. Für die Grenze von 800.000 € zählt
 * der Vorjahresumsatz der Agentur, weil sie die Gutschrift ausstellt (BMF-Schreiben vom 15.10.2024, Rn. 64).
 * 0345 (Entscheidung 16.10.2026, Punkt 4): Vorschlag aus den gebuchten Umsätzen 2026 der Agentur (Hausmuster der Umsatzsummen,
 * selfBillingEInvoiceSuggestion) mit Knopf „Übernehmen“; nichts wird automatisch gesetzt.
 */
export function GutschriftERechnung({ orgId }: { orgId: string }) {
  const country = useOrgCountry(orgId);
  const umsatz = useQuery({
    queryKey: ["invoices", orgId, "e_rechnung_stichtag", SELF_BILLING_E_INVOICE_BASE_YEAR],
    queryFn: () => loadOwnInvoicesYear(api, orgId, SELF_BILLING_E_INVOICE_BASE_YEAR),
    enabled: country.data === "DE",
  });
  const q = useQuery({
    queryKey: ["org_settings", orgId, "self_billing_e_invoice"],
    queryFn: () => api.select<{ self_billing_e_invoice_from: string | null }>("org_settings", { columns: "self_billing_e_invoice_from", filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null),
  });
  const [value, setValue] = useState<string | null>(null);
  const { busy, error, attempt } = useAction();
  const current = q.data?.self_billing_e_invoice_from ?? "";
  const v = value ?? current;
  const valid = !v || (/^\d{4}-\d{2}-\d{2}$/.test(v) && v >= "2025-01-01");
  const save = (next: string | null) => attempt("gs-xml", () => api.update("org_settings", [["org_id", "eq", orgId]], { self_billing_e_invoice_from: next }),
    t("Gespeichert.", "Saved.")).then((ok) => { if (ok) setValue(null); });
  const effective = current || SELF_BILLING_E_INVOICE_DEFAULT_FROM;
  const hinweis = umsatz.data ? gutschriftStichtagHinweis(selfBillingEInvoiceSuggestion(umsatz.data, { today: today(), agencyCountry: country.data, currentFrom: current || null })) : null;
  return (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); if (valid) void save(v || null); }}>
      <h4>{t("E-Rechnung zur Gutschrift", "E-invoice with self-billing invoices")}</h4>
      <p>{t(`Gutschriften an Creator in Deutschland, die keine Kleinunternehmer sind, gehen ab ${date(effective)} zusätzlich als E-Rechnung (XRechnung) raus.`,
        `Self-billing invoices to creators in Germany who are not small businesses also go out as an e-invoice (XRechnung) from ${date(effective)}.`)}
        {current ? "" : t(" Das ist die Vorgabe.", " This is the default.")}</p>
      <label>
        {t("E-Rechnung ab", "E-invoice from")}<input type="date" value={v} min="2025-01-01" onChange={(e) => setValue(e.target.value)} aria-describedby="gs-xml-hinweis" aria-invalid={!valid} />
      </label>
      <small id="gs-xml-hinweis">{t(
        "Leer: ab 1. Januar 2028, dann ist die E-Rechnung für alle Pflicht. Lag der Umsatz der Agentur im Vorjahr über 800.000 €, gilt die Pflicht schon ab 1. Januar 2027; bei Gutschriften zählt der Umsatz der Agentur, nicht der des Creators. Ein früherer Tag schaltet es schon jetzt ein. Creator in Österreich, der Schweiz oder anderen Ländern und Kleinunternehmer bekommen weiter nur das PDF. Je Creator änderbar im Creator-Profil unter Gutschriftverfahren.",
        "Empty: from 1 January 2028, when the e-invoice becomes mandatory for everyone. If the agency's turnover in the previous year was above €800,000, the obligation applies from 1 January 2027; for self-billing invoices the agency's turnover counts, not the creator's. An earlier date switches it on now. Creators in Austria, Switzerland or other countries and small businesses keep receiving only the PDF. Can be changed per creator in the creator profile under self-billing.")}</small>
      {/* Steuerprüfung L11: derselbe Tag gilt für Rechnungen an Marken in Deutschland, ohne Eintrag dort vorsichtig 1.1.2027 */}
      <p className="muted" id="marken-xml-hinweis">{t(
        `Derselbe Tag gilt für Rechnungen der Agentur an Marken in Deutschland, die Unternehmer sind (über 250 € brutto): ab dann gehen sie zusätzlich als XRechnung raus. Ohne Eintrag gilt dort vorsichtig der 1. Januar 2027${current ? "" : ", also früher als bei den Gutschriften"}; der Schalter „E-Rechnung“ an der Marke schaltet sie auch vorher schon ein.`,
        `The same date applies to the agency's invoices to brands in Germany that are businesses (above €250 gross): from then on they also go out as an XRechnung. Without an entry, 1 January 2027 applies there as a precaution${current ? "" : ", i.e. earlier than for self-billing invoices"}; the “E-invoice” switch on the brand turns it on even before.`)}</p>
      {hinweis && (
        <div className={hinweis.tone === "warning" ? "notice warning stack" : "notice stack"} role="status">
          <span>{hinweis.text}</span>
          <small>{hinweis.outside}</small>
          {hinweis.offer && (
            <div className="row">
              <button type="button" disabled={!!busy} onClick={() => void save(hinweis.offer)}>{t("Übernehmen", "Apply")}</button>
            </div>
          )}
        </div>
      )}
      {!valid && <p className="tk-error" role="alert">{t("Bitte ein Datum ab dem 1. Januar 2025 wählen oder leer lassen.", "Please choose a date from 1 January 2025 or leave empty.")}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="submit" disabled={!!busy || value === null || value === current || !valid}>{t("Speichern", "Save")}</button>
        {current && <button type="button" disabled={!!busy} onClick={() => void save(null)}>{t("Vorgabe (1. Januar 2028)", "Default (1 January 2028)")}</button>}
      </div>
    </form>
  );
}
